// frontend/src/components/dashboard/widgets/OverdueFollowupsWidget.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../../utils/secureAxios";
import { useAuth } from "../../../contexts/AuthContext";
import WidgetCard, { WidgetSuccessState } from "./WidgetCard";

const OverdueFollowupsWidget = ({ onlyMyData }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const hasFetchedRef = useRef(false);
  const lastParamsRef = useRef(null);

  useEffect(() => {
    const fetchOverdueFollowups = async () => {
      if (!user?.user_id || !user?.tenant_id) {
        setLoading(false);
        return;
      }

      const paramsKey = `${user.user_id}-${user.tenant_id}-${onlyMyData}`;

      if (hasFetchedRef.current && lastParamsRef.current === paramsKey) {
        return;
      }

      hasFetchedRef.current = true;
      lastParamsRef.current = paramsKey;
      setLoading(true);

      try {
        const response = await secureAxios.get(
          "/api/widgets/overdue-followups",
          {
            params: {
              only_my_data: onlyMyData,
            },
          }
        );
        setData(response.data || []);
        setError(false);
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error fetching overdue followups:", error);
        }
        setError(true);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOverdueFollowups();
  }, [user?.user_id, user?.tenant_id, onlyMyData]);

  const handleViewFollowup = (jobPlacementId, followupId) => {
    navigate(
      `/crm/job-placement?job_placement_id=${jobPlacementId}&followup_id=${followupId}&action=edit-followup`
    );
  };

  // Format the overdue period for display
  const formatOverduePeriod = (period) => {
    switch (period) {
      case "week1":
        return "Week 1";
      case "days30":
        return "30 Days";
      case "days60":
        return "60 Days";
      case "days90":
        return "90 Days";
      default:
        return period;
    }
  };

  // Calculate days overdue
  const getDaysOverdue = (dueDate) => {
    if (!dueDate) return 0;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Get urgency badge color based on days overdue
  const getUrgencyColor = (daysOverdue) => {
    if (daysOverdue > 14) return "danger";
    if (daysOverdue > 7) return "warning";
    return "info";
  };

  // Show success state when no overdue followups
  if (!loading && !error && data.length === 0) {
    return (
      <WidgetCard
        title="Follow-Up Activity"
        icon="bi-calendar-check"
        iconColor="success"
        loading={loading}
      >
        <WidgetSuccessState message="All CRP-32 follow-up activities are up to date!" />
      </WidgetCard>
    );
  }

  const subtitle = onlyMyData ? "(my consumers only)" : "";

  return (
    <WidgetCard
      title="Overdue Follow-Ups"
      subtitle={
        !loading && !error
          ? `CRP-32 activities needing attention ${subtitle}`
          : undefined
      }
      icon="bi-exclamation-triangle"
      iconColor="warning"
      loading={loading}
      error={error}
      errorMessage="Unable to load overdue follow-ups"
      badge={data.length > 0 ? data.length : undefined}
      badgeColor="warning"
    >
      {/* Desktop/tablet table view */}
      <div className="d-none d-md-block">
        <div className="table-responsive">
          <table className="table table-sm table-bordered widget-table">
            <thead>
              <tr>
                <th>Consumer</th>
                <th>Period</th>
                <th>Due Date</th>
                <th>Overdue</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const daysOverdue = getDaysOverdue(item.due_date);
                const urgencyColor = getUrgencyColor(daysOverdue);
                return (
                  <tr
                    key={`${item.followup_id}-${item.overdue_period}-${index}`}
                  >
                    <td className="widget-consumer-name">
                      {item.consumer_name}
                    </td>
                    <td>
                      <span className={`badge bg-${urgencyColor}`}>
                        {formatOverduePeriod(item.overdue_period)}
                      </span>
                    </td>
                    <td>
                      <small>
                        {item.due_date
                          ? new Date(item.due_date).toLocaleDateString()
                          : "N/A"}
                      </small>
                    </td>
                    <td>
                      <span className={`text-${urgencyColor} fw-bold`}>
                        {daysOverdue} day{daysOverdue !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          handleViewFollowup(
                            item.job_placement_id,
                            item.followup_id
                          )
                        }
                        aria-label={`View follow-up for ${item.consumer_name}`}
                      >
                        <i className="bi bi-pencil-square me-1"></i>
                        Update
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile-friendly list view */}
      <div className="d-block d-md-none">
        {data.map((item, index) => {
          const daysOverdue = getDaysOverdue(item.due_date);
          const urgencyColor = getUrgencyColor(daysOverdue);
          return (
            <div
              key={`${item.followup_id}-${item.overdue_period}-${index}`}
              className={`widget-mobile-card widget-mobile-card--${urgencyColor} border bg-light`}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="fw-bold">{item.consumer_name}</div>
                  <div className="small">
                    <span className={`badge bg-${urgencyColor} me-2`}>
                      {formatOverduePeriod(item.overdue_period)}
                    </span>
                    <span className={`text-${urgencyColor}`}>
                      {daysOverdue} day{daysOverdue !== 1 ? "s" : ""} overdue
                    </span>
                  </div>
                  <div className="small text-muted">
                    Due:{" "}
                    {item.due_date
                      ? new Date(item.due_date).toLocaleDateString()
                      : "N/A"}
                  </div>
                  {item.placement_specialist && (
                    <div className="small text-muted">
                      Specialist: {item.placement_specialist}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm btn-outline-primary ms-2"
                  onClick={() =>
                    handleViewFollowup(item.job_placement_id, item.followup_id)
                  }
                  aria-label={`Update follow-up for ${item.consumer_name}`}
                >
                  <i className="bi bi-pencil-square"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {data.length > 5 && (
        <div className="text-center mt-3">
          <small className="text-muted">
            Showing {data.length} overdue follow-up
            {data.length !== 1 ? "s" : ""}
          </small>
        </div>
      )}
    </WidgetCard>
  );
};

export default OverdueFollowupsWidget;
